import { Slot, ClassNote, Violation, Class, ClassNoteLesson } from '@/plugins/api'
import moment from 'moment'
import { get } from 'lodash'

export default {
  namespaced: true,
  state: {
    slot: {},
    violations: [],
    loading: false,
    currentClassNote: {},
    classData: {},
    studentClassCount: 0,
    date: moment().startOf('day').toISOString()
  },
  actions: {
    async fetchData({ commit, state }, { slotId }) {
      const slot = await Slot.fetchOne(slotId)
      const classData = await Class.fetchOne(slot.class.id)
      const studentClassCount = classData.students.filter(s => s.status === 'active').length

      let currentClassNote = {}
      if (get(slot, 'classNotes.length')) {
        currentClassNote = slot.classNotes.find(classNote => classNote.date === state.date)
      }
      if (currentClassNote && currentClassNote.id) {
        currentClassNote = await ClassNote.fetchOne(currentClassNote.id)
        const violations = await Promise.all(currentClassNote.violations.map(v => Violation.fetchOne(v.id)))
        commit('changeState', {
          violations
        })
      }

      let currentTopicLesson = {}
      if (currentClassNote && currentClassNote.id) {
        try {
          currentTopicLesson = await ClassNoteLesson.getLessonByClassNote({
            classNote: currentClassNote.id
          })
        } catch (err) {
          const isNotFound =
            err &&
            ((err.response && err.response.status === 404) ||
              (err.status && err.status === 404) ||
              /not\s+found/i.test(err.message || ''))
          if (!isNotFound) {
            console.error('Error fetching lesson by class note:', err)
          }
          currentTopicLesson = {}
        }
      }

      commit('changeState', {
        slot: { ...slot, classNote: currentClassNote },
        currentClassNote,
        classData,
        studentClassCount,
        currentTopicLesson
      })
    },
    async updateLesson({ commit, state }, params) {
      let classNote
      let classNoteLesson
      if (get(state.currentClassNote, 'id')) {
        classNote = await ClassNote.update(state.currentClassNote.id, {
          lesson: params.lesson,
          data: {
            ...get(state.currentClassNote, 'data', {}),
            lessonIndex: params.data.lessonIndex
          }
        })

        if (get(state.currentTopicLesson, '_id')) {
          classNoteLesson = await ClassNoteLesson.update(state.currentTopicLesson._id, {
            lessonIndex: params.data.lessonIndex,
            topicLesson: params.topicLesson,
            classNote: state.currentClassNote.id
          })
        } else {
          classNoteLesson = await ClassNoteLesson.create({
            lessonIndex: params.data.lessonIndex,
            topicLesson: params.topicLesson,
            classNote: state.currentClassNote.id
          })
        }
      } else {
        const isLated = moment().isAfter(moment().startOf('day').add(21, 'hours'))
        classNote = await ClassNote.create({ ...params, status: isLated ? 'late' : 'on-time' })

        let classNoteIds = []
        if (get(state.slot, 'classNotes.length')) {
          classNoteIds = [...state.slot.classNotes.map(cn => cn.id), classNote.id]
        } else {
          classNoteIds = [classNote.id]
        }

        await Slot.update(state.slot.id, {
          classNotes: classNoteIds
        })
      }

      const slot = { ...state.slot, classNote }
      commit('changeState', {
        slot,
        currentClassNote: classNote,
        currentTopicLesson: classNoteLesson
      })
    },
    async updateClassComment({ commit, state }, params) {
      let classNote
      if (get(state.currentClassNote, 'id')) {
        classNote = await ClassNote.update(state.slot.classNote.id, {
          data: {
            ...get(state.currentClassNote, 'data', {}),
            classMark: params.data.classMark,
            classComment: params.data.classComment
          }
        })
      } else {
        const isLated = moment().isAfter(moment().startOf('day').add(21, 'hours'))
        classNote = await ClassNote.create({ ...params, status: isLated ? 'late' : 'on-time' })

        let classNoteIds = []
        if (get(state.slot, 'classNotes.length')) {
          classNoteIds = [...state.slot.classNotes.map(cn => cn.id), classNote.id]
        } else {
          classNoteIds = [classNote.id]
        }

        await Slot.update(state.slot.id, {
          classNotes: classNoteIds
        })
      }

      const slot = { ...state.slot, classNote }
      commit('changeState', {
        slot,
        currentClassNote: classNote
      })
    },
    async updateClassReminder({ commit, state }, params) {
      let classNote
      if (get(state.currentClassNote, 'id')) {
        classNote = await ClassNote.update(state.slot.classNote.id, {
          data: {
            ...get(state.currentClassNote, 'data', {}),
            classReminder: params.data.classReminder
          }
        })
      } else {
        const isLated = moment().isAfter(moment().startOf('day').add(21, 'hours'))
        classNote = await ClassNote.create({ ...params, status: isLated ? 'late' : 'on-time' })

        let classNoteIds = []
        if (get(state.slot, 'classNotes.length')) {
          classNoteIds = [...state.slot.classNotes.map(cn => cn.id), classNote.id]
        } else {
          classNoteIds = [classNote.id]
        }

        await Slot.update(state.slot.id, {
          classNotes: classNoteIds
        })
      }

      const slot = { ...state.slot, classNote }
      commit('changeState', {
        slot,
        currentClassNote: classNote
      })
    },
    async createViolation({ commit, state }, params) {
      const violation = await Violation.create({
        ...params.violationData,
        date: state.date
      })
      let classNote
      if (get(state.currentClassNote, 'id')) {
        classNote = await ClassNote.update(state.currentClassNote.id, {
          violations: [...state.currentClassNote.violations, violation.id]
        })
      } else {
        const isLated = moment().isAfter(moment().startOf('day').add(21, 'hours'))
        classNote = await ClassNote.create({
          violations: [violation.id],
          ...params.classNoteDetailData,
          status: isLated ? 'late' : 'on-time'
        })

        let classNoteIds = []
        if (get(state.slot, 'classNotes.length')) {
          classNoteIds = [...state.slot.classNotes.map(cn => cn.id), classNote.id]
        } else {
          classNoteIds = [classNote.id]
        }

        await Slot.update(state.slot.id, {
          classNotes: classNoteIds
        })
      }

      const slot = { ...state.slot, classNote }
      commit('changeState', {
        slot,
        violations: [...state.violations, violation],
        currentClassNote: classNote
      })
    },
    async mappingMark({ commit, state }, params) {
      if (params.markIds && params.markIds.length) {
        let classNote
        if (get(state.currentClassNote, 'id')) {
          const currentMarks = state.currentClassNote.marks || []
          const markIds =
            currentMarks && currentMarks.length ? [...state.currentClassNote.marks, ...params.markIds] : params.markIds
          classNote = await ClassNote.update(state.currentClassNote.id, {
            marks: markIds
          })
        } else {
          const isLated = moment().isAfter(moment().startOf('day').add(21, 'hours'))
          classNote = await ClassNote.create({
            marks: params.markIds,
            ...params.classNoteDetailData,
            status: isLated ? 'late' : 'on-time'
          })

          let classNoteIds = []
          if (get(state.slot, 'classNotes.length')) {
            classNoteIds = [...state.slot.classNotes.map(cn => cn.id), classNote.id]
          } else {
            classNoteIds = [classNote.id]
          }

          await Slot.update(state.slot.id, {
            classNotes: classNoteIds
          })
        }

        const slot = { ...state.slot, classNote }
        commit('changeState', {
          slot,
          currentClassNote: classNote
        })
      }
    },
    async updateViolationList({ commit, state }) {
      if (get(state.currentClassNote, 'violations')) {
        const violations = await Promise.all(state.currentClassNote.violations.map(v => Violation.fetchOne(v.id)))
        commit('changeState', {
          violations
        })
      }
    },
    deleteViolation({ commit, state }, violationId) {
      const violations = state.violations.filter(v => v.id !== violationId)
      commit('changeState', {
        violations
      })
    },
    setDate({ commit }, date) {
      commit('changeState', {
        date
      })
    }
  },
  mutations: {},
  getters: {
    classNoteDetailData: state => {
      const slot = { ...state.slot }
      delete slot.classNotes

      return {
        date: state.date,
        class: get(slot, 'class.id'),
        teacher: get(slot, 'teaching.teacher') || get(slot, 'teacher.id'),
        subject: get(slot, 'teaching.subject') || get(slot, 'subject.id'),
        index: slot.index,
        day: slot.day,
        indexDay: slot.indexDay,
        metadata: {
          auto: false,
          slot
        }
      }
    }
  }
}
